import { useEffect, useState } from "react";
import { Check, Loader2, ShieldCheck, Sparkles } from "lucide-react";
import ArqNavigation from "@/components/arqera/ArqNavigation";
import ArqFooter from "@/components/arqera/ArqFooter";
import { SEO } from "@/components/SEO";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

type Segment = "healthcare-checklist-download" | "builder-checklist-download" | "sample-deliverable-download";

const SEGMENT_PDF: Record<Segment, string> = {
  "healthcare-checklist-download": "arqeraai-hipaa-ai-risk-checklist-2026.pdf",
  "builder-checklist-download": "arqeraai-prelaunch-ai-security-checklist.pdf",
  "sample-deliverable-download": "arqeraai-sample-redacted-report.pdf",
};

interface CardConfig {
  segment: Segment;
  badge: string;
  badgeTone: "blue" | "teal";
  headline: string;
  subheadline: string;
  description: string;
  bullets: string[];
  emailPlaceholder: string;
  orgLabel: string;
  orgPlaceholder: string;
}

const CARDS: CardConfig[] = [
  {
    segment: "healthcare-checklist-download",
    badge: "FOR COMPLIANCE OFFICERS",
    badgeTone: "blue",
    headline: "The 2026 HIPAA AI Risk Checklist",
    subheadline: "12 Questions to Ask Before Your Next OCR Audit",
    description:
      "Score your organization's AI compliance posture across vendor contracts, technical security, and board readiness. Know your exposure before a regulator asks.",
    bullets: [
      "12 scored assessment questions",
      "Regulatory context for each question",
      "Scoring guide with prioritized next steps",
      "OCR audit readiness framework",
    ],
    emailPlaceholder: "your@organization.com",
    orgLabel: "Organization",
    orgPlaceholder: "Hospital, system, or practice",
  },
  {
    segment: "builder-checklist-download",
    badge: "FOR AI FOUNDERS",
    badgeTone: "teal",
    headline: "The Pre-Launch AI Security Checklist",
    subheadline: "What Enterprise Clients Will Ask Before Signing",
    description:
      "Work through 10 categories of enterprise security requirements before your next procurement conversation. Know your gaps before they stall your deal.",
    bullets: [
      "10 enterprise security categories",
      "Specific questions buyers ask, and what they mean",
      "Readiness guide with prioritized next steps",
      "Documentation package checklist",
    ],
    emailPlaceholder: "your@company.com",
    orgLabel: "Company / Product Name",
    orgPlaceholder: "Your company or product",
  },
];

function ResourceCard({ cfg }: { cfg: CardConfig }) {
  const [form, setForm] = useState({ email: "", firstName: "", organization: "", honeypot: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("send-resource-email", {
        body: { segment: cfg.segment, ...form },
      });
      if (error) throw error;
      // Trigger browser download
      const url = `/resources/${SEGMENT_PDF[cfg.segment]}`;
      const a = document.createElement("a");
      a.href = url;
      a.download = SEGMENT_PDF[cfg.segment];
      document.body.appendChild(a);
      a.click();
      a.remove();
      setDone(true);
      toast({ title: "Checklist on its way", description: "Download started. We just emailed you a copy as well." });
    } catch (err: any) {
      toast({ title: "Something went wrong", description: err?.message ?? "Please try again.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const badgeClass =
    cfg.badgeTone === "blue"
      ? "bg-arq-blue/10 border-arq-blue/30 text-arq-blue"
      : "bg-[hsl(167_85%_35%/0.1)] border-[hsl(167_85%_35%/0.4)] text-[hsl(167_85%_45%)]";

  return (
    <div className="bg-arq-surface1 border border-arq-border rounded-2xl p-8 flex flex-col h-full">
      <div className={`inline-flex self-start items-center gap-2 px-3 py-1 rounded-full text-[11px] font-mono tracking-wider border ${badgeClass} mb-6`}>
        {cfg.badge}
      </div>
      <h3 className="font-display text-2xl md:text-[26px] font-semibold text-arq-text leading-tight mb-2">{cfg.headline}</h3>
      <p className="text-arq-muted text-[15px] mb-4">{cfg.subheadline}</p>
      <p className="text-arq-text/80 text-[15px] leading-relaxed mb-5">{cfg.description}</p>
      <ul className="space-y-2 mb-7">
        {cfg.bullets.map((b) => (
          <li key={b} className="flex items-start gap-2 text-[14px] text-arq-text/85">
            <Check className="w-4 h-4 mt-0.5 flex-shrink-0 text-[hsl(167_85%_45%)]" aria-hidden />
            <span>{b}</span>
          </li>
        ))}
      </ul>

      {done ? (
        <div className="mt-auto rounded-xl border border-[hsl(167_85%_35%/0.4)] bg-[hsl(167_85%_35%/0.08)] p-5 text-arq-text">
          <div className="flex items-center gap-2 mb-2 text-[hsl(167_85%_55%)] font-semibold">
            <ShieldCheck className="w-5 h-5" /> Sent
          </div>
          <p className="text-sm text-arq-text/85">Your download started. Check your inbox for the email copy and follow-up resources over the next week.</p>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="mt-auto space-y-3">
          <input type="text" name="company_url" autoComplete="off" tabIndex={-1} value={form.honeypot} onChange={set("honeypot")} className="hidden" aria-hidden />
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Work email" type="email" required value={form.email} onChange={set("email")} placeholder={cfg.emailPlaceholder} autoComplete="email" />
            <Field label="First name" required value={form.firstName} onChange={set("firstName")} placeholder="Jane" autoComplete="given-name" />
          </div>
          <Field label={cfg.orgLabel} required value={form.organization} onChange={set("organization")} placeholder={cfg.orgPlaceholder} autoComplete="organization" />
          
          <button
            type="submit"
            disabled={loading}
            className="w-full mt-2 inline-flex items-center justify-center gap-2 bg-arq-blue text-white font-medium px-5 py-3 rounded-lg hover:bg-arq-blue/90 transition-colors disabled:opacity-60"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {loading ? "Sending…" : cfg.segment === "sample-deliverable-download" ? "Download sample report" : "Download free checklist"}
          </button>
          <p className="text-[12px] text-arq-muted text-center">No spam. Unsubscribe anytime. We will never share your information.</p>
        </form>
      )}
    </div>
  );
}

function Field({ label, ...props }: { label: string } & React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <label className="block">
      <span className="block text-[12px] font-mono tracking-wide text-arq-muted mb-1.5">{label.toUpperCase()}</span>
      <input
        {...props}
        className="w-full bg-arq-bg border border-arq-border rounded-lg px-3 py-2.5 text-[14px] text-arq-text placeholder:text-arq-muted/60 focus:outline-none focus:border-arq-blue transition-colors"
      />
    </label>
  );
}

export default function Resources() {
  useEffect(() => {
    document.title = "Free AI Security Resources - ArqeraAI";
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute("content", "Free checklists from ArqeraAI: a 2026 HIPAA AI Risk Checklist for healthcare compliance officers, and a Pre-Launch AI Security Checklist for technical founders.");
  }, []);

  return (
    <div className="min-h-screen bg-arq-bg text-arq-text">
      <SEO
        title="Free AI Security Resources, ArqeraAI"
        description="Free AI security checklists for compliance officers and technical founders. Score your posture before a regulator or buyer does."
        canonical="https://arqeraai.com/resources"
      />
      <ArqNavigation />

      <main>
        {/* Hero */}
        <section className="relative pt-32 pb-16 md:pt-40 md:pb-20 px-6 overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,hsl(217_91%_60%/0.1),transparent_60%)]" aria-hidden />
          <div className="relative max-w-[1200px] mx-auto text-center">
            <div className="arq-label mb-6">FREE RESOURCES</div>
            <h1 className="font-display font-semibold text-arq-text mb-5" style={{ fontSize: "clamp(32px, 5vw, 56px)", lineHeight: 1.1 }}>
              Tools to Assess Your AI Risk, Before Someone Else Does.
            </h1>
            <p className="text-arq-muted text-[17px] md:text-[18px] max-w-2xl mx-auto leading-relaxed">
              Two free checklists built for compliance officers and technical founders. Enter your work email and download instantly.
            </p>
          </div>
        </section>

        {/* Cards */}
        <section className="px-6 pb-12">
          <div className="max-w-[1200px] mx-auto grid md:grid-cols-2 gap-6">
            {CARDS.map((c) => (
              <ResourceCard key={c.segment} cfg={c} />
            ))}
          </div>
        </section>

        {/* Sample deliverable, gated download */}
        <section className="px-6 pb-12">
          <div className="max-w-[1200px] mx-auto">
            <ResourceCard
              cfg={{
                segment: "sample-deliverable-download",
                badge: "SAMPLE DELIVERABLE",
                badgeTone: "blue",
                headline: "Redacted Findings & Validation Report",
                subheadline: "What an ArqeraAI engagement actually ships",
                description:
                  "The redacted report from a real Healthcare SaaS engagement: executive summary with dollar exposure, a sample detailed finding mapped to OWASP, MITRE ATLAS, and HIPAA, the validation log, and the evidence package index your auditor and customer security review will both see.",
                bullets: [
                  "Executive summary with regulatory citations and dollar exposure",
                  "Sample Critical finding with reproduction, remediation, and validation",
                  "Three-phase methodology (Assess · Remediate · Validate)",
                  "Evidence package index for auditors, customers, and insurance carriers",
                ],
                emailPlaceholder: "your@organization.com",
                orgLabel: "Organization",
                orgPlaceholder: "Your company or organization",
              }}
            />
          </div>
        </section>

        {/* Bottom CTA */}
        <section className="bg-arq-surface1 border-t border-arq-border py-20 px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-display text-3xl md:text-4xl font-semibold text-arq-text mb-4">Not sure which applies to you?</h2>
            <p className="text-arq-muted text-[17px] mb-7 leading-relaxed">
              Schedule a free 30-minute AI Security Posture Review. We will assess your situation and tell you exactly what your highest-risk exposure is.
            </p>
            <a
              href="https://calendly.com/hello-arqeraai/30min"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center bg-arq-blue text-white font-medium px-7 py-3.5 rounded-lg hover:bg-arq-blue/90 transition-colors"
            >
              Schedule free posture review
            </a>
          </div>
        </section>
      </main>

      <ArqFooter />
    </div>
  );
}
